<template>
    <div class="content-card" 
        :class="{ 
            'primary-card': showPrimaryCard && isPrimaryAsset,
            'selected': isSelected,
            'collapsed': isCollapsed,
            'is-generating': touchpoint.status == 'generating'
        }"
        @click="handleCardClick">
        
        <!-- Primary card hero image: top 90px when asset has image input with value -->
        <div v-if="!isCollapsed && showPrimaryCard && isPrimaryAsset && primaryHeroImageUrl" class="content-card-hero">
            <img :src="primaryHeroImageUrl" alt="" class="content-card-hero-img">
        </div>

        <div class="content-card-body d-flex flex-column">
            <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <img :src="'/img/icons/avid-icons/' + (touchpoint.asset_type?.icon_class || 'email') + '.svg'" 
                        class="asset-type-icon icon" 
                        data-bs-toggle="tooltip" 
                        :title="touchpoint.asset_type ? touchpoint.asset_type.name : 'Touchpoint type'">
                    <h5 class="mb-0 ms-2">{{ touchpoint.name || 'Untitled Touchpoint' }}</h5>
                </div>
                <div class="d-flex align-items-center">
                    <span v-if="showLaunchDateInHeader && launchDateTime" class="small text-muted me-2">Go Live {{ launchDateTime }}</span>
                    <button v-if="isCollapsed" class="btn btn-icon-only-sm btn-white" @click.stop="handleToggle" :disabled="touchpoint.status == 'generating'" data-bs-toggle="tooltip" title="Expand">
                        <img src="/img/icons/avid-icons/expand.svg" class="icon" alt="">
                    </button>
                </div>
            </div>
            
            <!-- Expanded Content (only show when not collapsed) -->
            <div v-if="!isCollapsed" class="flex-grow-1 d-flex flex-column mt-3">
                <!-- <div class="d-flex align-items-center label-text" v-if="touchpoint.description">
                    <img src="/img/icons/avid-icons/text.svg" style="width: 14px; height: 14px;" class="icon me-2" data-bs-toggle="tooltip" title="Description" alt="">
                    <span class="small">{{ touchpoint.description }}</span>
                </div> -->
                <div class="d-flex align-items-center label-text">
                    <img src="/img/icons/avid-icons/target-audience.svg" class="icon me-2" data-bs-toggle="tooltip" title="Target audience" alt="">
                    <span class="small">
                        {{ touchpoint.segment && touchpoint.segment.name ? touchpoint.segment.name : 'No audience attached' }}
                    </span>
                </div>

                <div class="d-flex align-items-center label-text" v-if="showLaunchDateInContent && touchpoint.launch_date">
                    <img src="/img/icons/avid-icons/calendar.svg" v-if="!editingLaunchDate" class="icon me-2" data-bs-toggle="tooltip" title="Launch date" alt="">
                    <span v-if="!editingLaunchDate && canEditLaunchDate" 
                          @click.stop="startEditLaunchDate" 
                          class="small editable-field" 
                          :class="{ 'saving': savingLaunchDate }" 
                          data-bs-toggle="tooltip" 
                          title="Click to edit">
                        {{ datetime(touchpoint.launch_date) }}
                    </span>
                    <span v-else-if="!editingLaunchDate" class="small">
                        {{ datetime(touchpoint.launch_date) }}
                    </span>
                    <span v-else class="d-inline-block" ">
                        <DatetimePickerLocal 
                            v-model="tempLaunchDate"
                            @update:modelValue="handleLaunchDateChange"
                            placeholder="Select launch date and time"
                            input-format="MM/dd/yyyy hh:mm a"
                            input-class="form-control form-control-sm"
                        />
                    </span>
                </div>
                
                <div v-if="showPrimaryInputs && isPrimaryAsset && visibleInputs.length > 0" class="mt-3 touchpoint-inputs-container">
                    <div v-for="input in visibleInputs" :key="input.placeholder" class="mb-3 touchpoint-content">
                        <div v-if="input.placeholder != 'citations'">
                            <div class="touchpoint-content-label mb-2">{{ getPromptLabel(input.placeholder, touchpoint.asset_type) }}</div>
                            <template v-if="isImageInput(input.placeholder)">
                                <div class="touchpoint-input-images d-flex flex-wrap gap-2 align-items-center">
                                    <img v-for="(imgUrl, idx) in getImageUrls(input.value)" :key="idx" :src="imgUrl" alt="" class="touchpoint-input-img">
                                </div>
                            </template>
                            <div v-else v-html="formatPromptValue(input.value, input.placeholder)" class="touchpoint-content-body mb-3"></div>
                        </div>
                    </div>
                </div>
                
                <div class="d-flex justify-content-between align-items-center mt-auto card-bottom">
                    <div class="d-flex align-items-center mt-2">
                        <button v-if="isCollapsed" class="btn btn-icon-only-sm btn-white" @click.stop="handleToggle" data-bs-toggle="tooltip" title="Expand">
                            <img src="/img/icons/avid-icons/expand.svg" class="icon" alt="">
                        </button>
                        <span class="badge mx-0" 
                              :class="touchpointStatus.classes"
                              :data-bs-toggle="touchpointStatus.label === 'Incomplete' ? 'tooltip' : null"
                              :title="touchpointStatus.label === 'Incomplete' ? 'There are required inputs that are empty' : null">{{ touchpointStatus.label }}</span>
                    </div>

                    <div class="touchpoint-actions mt-2">
                        <button class="btn btn-icon-only-sm btn-white" @click.stop="handleViewAssetTypeInfo" :disabled="touchpoint.status == 'generating'"
                            data-bs-toggle="tooltip" title="Detailed view with extended insights and context">
                            <img src="/img/icons/avid-icons/deep-dive.svg" class="icon" alt="">
                        </button>
                        <button class="btn btn-icon-only-sm btn-white" @click.stop="handleOpenMiniMenu" :disabled="touchpoint.status == 'generating'" data-bs-toggle="tooltip" title="Actions">
                            <img src="/img/icons/avid-icons/mini-menu.svg" class="icon" alt="">
                        </button>
                        <button v-if="canEdit('playbooks')" class="btn btn-icon-only-sm btn-white" @click.stop="handleEdit" :disabled="touchpoint.status == 'generating'" data-bs-toggle="tooltip" title="Edit touchpoint">
                            <img src="/img/icons/avid-icons/edit.svg" class="icon" alt="">
                        </button>
                        <button v-else class="btn btn-icon-only-sm btn-white" :disabled="true" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                            <img src="/img/icons/avid-icons/edit.svg" class="icon" alt="">
                        </button>
                        <button class="btn btn-icon-only-sm btn-white" @click.stop="handleToggle" :disabled="touchpoint.status == 'generating'" data-bs-toggle="tooltip" title="Collapse">
                            <img src="/img/icons/avid-icons/collapse.svg" class="icon" alt="">
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mini Menu Overlay (only show when this card's menu is open) -->
        <div v-if="showMiniMenu" class="mini-menu-overlay" @click="handleCardClick">
            <div class="mini-menu" @click.stop>
                <div class="mini-menu-header" :class="{ 'pb-0': miniMenuHeaderClass }">
                    <span>Action Menu</span>
                    <button class="btn btn-icon-only-sm btn-white" @click="handleCloseMiniMenu" data-bs-toggle="tooltip" title="Close">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="mini-menu-actions" :class="{ 'two-column': miniMenuTwoColumn }">
                    <button v-if="canEdit('playbooks')" class="mini-menu-item" @click="handleApprove" :disabled="touchpoint.status == 'generating' || isDraft">
                        <img class="icon" src="/img/icons/avid-icons/check-01.svg" alt="">Approve
                    </button>
                    <button v-if="canExport('playbooks')" class="mini-menu-item" @click="handleDeploy" :disabled="touchpoint.status == 'generating' || isDraft">
                        <img class="icon" src="/img/icons/avid-icons/rocket-02.svg" alt="">Launch
                    </button>
                    <button v-else class="mini-menu-item" :disabled="true" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <img class="icon" src="/img/icons/avid-icons/rocket-02.svg" alt="">Launch
                    </button>
                    <button v-if="canEdit('playbooks')" class="mini-menu-item" @click="handleEdit" :disabled="touchpoint.status == 'generating'">
                        <img class="icon" src="/img/icons/avid-icons/pencil.svg" alt="">Edit
                    </button>
                    <button v-if="canEdit('playbooks')" class="mini-menu-item" @click="handleDuplicate" :disabled="touchpoint.status == 'generating'">
                        <img class="icon" src="/img/icons/avid-icons/document-copy.svg" alt="">Duplicate
                    </button>
                    <button v-if="canExport('playbooks')" class="mini-menu-item" @click="handleReview" :disabled="touchpoint.status == 'generating'">
                        <img class="icon" src="/img/icons/avid-icons/download.svg" alt="">Review
                    </button>
                    <button v-if="canEdit('playbooks')" class="mini-menu-item delete" @click="handleDelete" :disabled="touchpoint.status == 'generating'">
                        <img class="icon red-icon" src="/img/icons/avid-icons/trash.svg" alt="">Delete
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import permissionsMixin from '../../mixins/permissions';
import filtersMixin from '../../mixins/filters';
import DatetimePickerLocal from '../components/DatetimePickerLocal.vue';

export default {
    name: 'TouchpointContentCard',
    mixins: [permissionsMixin, filtersMixin],
    components: {
        DatetimePickerLocal
    },
    props: {
        touchpoint: {
            type: Object,
            required: true
        },
        isCollapsed: {
            type: Boolean,
            default: false
        },
        isSelected: {
            type: Boolean,
            default: false
        },
        showMiniMenu: {
            type: Boolean,
            default: false
        },
        isPrimaryAsset: {
            type: Boolean,
            default: false
        },
        showPrimaryCard: {
            type: Boolean,
            default: false
        },
        showPrimaryInputs: {
            type: Boolean,
            default: false
        },
        showLaunchDateInHeader: {
            type: Boolean,
            default: false
        },
        showLaunchDateInContent: {
            type: Boolean,
            default: true
        },
        launchDateTime: {
            type: String,
            default: null
        },
        touchpointStatus: {
            type: Object,
            required: true
        },
        isDraft: {
            type: Boolean,
            default: false
        },
        miniMenuTwoColumn: {
            type: Boolean,
            default: false
        },
        miniMenuHeaderClass: {
            type: String,
            default: ''
        },
        editableLaunchDate: {
            type: Boolean,
            default: false
        }
    },
    emits: [
        'toggle',
        'view-asset-type-info',
        'open-mini-menu',
        'close-mini-menu',
        'edit',
        'approve',
        'deploy',
        'duplicate',
        'review',
        'delete',
        'card-click',
        'launch-date-updated'
    ],
    data() {
        return {
            editingLaunchDate: false,
            tempLaunchDate: null,
            originalLaunchDate: null,
            savingLaunchDate: false,
            imageWidths: {} // url -> naturalWidth for picking widest hero image
        };
    },
    computed: {
        canEditLaunchDate() {
            return this.editableLaunchDate && this.canEdit('playbooks');
        },
        normalizedInputs() {
            if (!this.touchpoint.inputs) return [];
            return Array.isArray(this.touchpoint.inputs)
                ? this.touchpoint.inputs
                : Object.values(this.touchpoint.inputs);
        },
        /** Inputs that are defined on the playbook asset type (filters out extra/unknown inputs). */
        visibleInputs() {
            const at = this.touchpoint.asset_type;
            if (!at) return [];
            const validPlaceholders = new Set();
            for (const i of at.inputs || []) {
                const p = i.placeholder ?? i.name;
                if (p) validPlaceholders.add(p);
            }
            for (const t of at.input_types || []) {
                const p = t.name ?? t.placeholder;
                if (p) validPlaceholders.add(p);
            }
            return this.normalizedInputs.filter(inp => validPlaceholders.has(inp.placeholder));
        },
        primaryHeroImageUrls() {
            if (!this.showPrimaryCard || !this.isPrimaryAsset || !this.touchpoint.asset_type?.inputs) return [];
            const urls = [];
            for (const input of this.visibleInputs) {
                if (!this.isImageInput(input.placeholder)) continue;
                for (const url of this.getImageUrls(input.value)) {
                    if (url) urls.push(url);
                }
            }
            return urls;
        },
        primaryHeroImageUrl() {
            const urls = this.primaryHeroImageUrls;
            if (urls.length === 0) return null;
            if (urls.length === 1) return urls[0];
            let widestUrl = urls[0];
            let maxW = this.imageWidths[widestUrl] || 0;
            for (const url of urls) {
                const w = this.imageWidths[url];
                if (w != null && w > maxW) {
                    maxW = w;
                    widestUrl = url;
                }
            }
            return widestUrl;
        }
    },
    watch: {
        primaryHeroImageUrls: {
            handler(urls) {
                if (urls.length === 0) return;
                urls.forEach(url => {
                    if (this.imageWidths[url] != null) return;
                    const img = new Image();
                    img.onload = () => {
                        this.imageWidths = { ...this.imageWidths, [url]: img.naturalWidth };
                    };
                    img.src = url;
                });
            },
            immediate: true
        }
    },
    methods: {
        handleToggle() {
            this.$emit('toggle', this.touchpoint);
        },
        handleViewAssetTypeInfo() {
            this.$emit('view-asset-type-info', this.touchpoint);
        },
        handleOpenMiniMenu() {
            this.$emit('open-mini-menu', this.touchpoint);
        },
        handleCloseMiniMenu() {
            this.$emit('close-mini-menu');
        },
        handleEdit() {
            this.$emit('edit', this.touchpoint);
        },
        handleApprove() {
            this.$emit('approve', this.touchpoint);
        },
        handleDeploy() {
            this.$emit('deploy', this.touchpoint);
        },
        handleDuplicate() {
            this.$emit('duplicate', this.touchpoint);
        },
        handleReview() {
            this.$emit('review', this.touchpoint);
        },
        handleDelete() {
            this.$emit('delete', this.touchpoint);
        },
        handleCardClick() {
            this.$emit('card-click', this.touchpoint);
        },
        startEditLaunchDate() {
            if (!this.canEditLaunchDate) return;
            this.originalLaunchDate = this.touchpoint.launch_date ? new Date(this.touchpoint.launch_date) : null;
            this.tempLaunchDate = this.originalLaunchDate;
            this.editingLaunchDate = true;
        },
        handleLaunchDateChange(newDate) {
            // DatetimePickerLocal emits a Date object
            // Save after a short delay to allow the datepicker to close
            this.$nextTick(() => {
                this.saveLaunchDate(newDate);
            });
        },
        saveLaunchDate(newDate) {
            if (!this.editingLaunchDate) return;
            
            // If the date was cleared (null), reset to original value and cancel editing
            if (!newDate) {
                this.tempLaunchDate = this.originalLaunchDate;
                this.editingLaunchDate = false;
                return;
            }
            
            // Check if date actually changed by comparing ISO strings
            const currentDateStr = this.touchpoint.launch_date ? new Date(this.touchpoint.launch_date).toISOString() : null;
            const newDateStr = newDate ? new Date(newDate).toISOString() : null;
            
            if (currentDateStr === newDateStr) {
                this.editingLaunchDate = false;
                return;
            }
            
            this.savingLaunchDate = true;
            
            // Emit the update event - parent component will handle the API call
            this.$emit('launch-date-updated', {
                touchpoint: this.touchpoint,
                newDate: newDate.toISOString()
            });
            
            // Reset editing state after a short delay
            setTimeout(() => {
                this.editingLaunchDate = false;
                this.savingLaunchDate = false;
            }, 500);
        },
        isImageInput(placeholder) {
            const at = this.touchpoint.asset_type;
            if (!at) return false;
            const fromInputs = (at.inputs || []).find(i => (i.placeholder || i.name) === placeholder);
            if (fromInputs) return fromInputs.type === 'image';
            const fromTypes = (at.input_types || []).find(t => (t.name || t.placeholder) === placeholder);
            return fromTypes?.type === 'image';
        },
        getImageUrls(value) {
            if (value == null) return [];
            if (Array.isArray(value)) {
                return value.filter(v => v && typeof v === 'string');
            }
            return typeof value === 'string' ? [value] : [];
        },
        getPromptLabel(placeholder, assetType) {
            // Get the title from asset_type object, or use placeholder as fallback
            if (assetType && assetType.input_types) {
                const inputType = assetType.input_types.find(type => type.name === placeholder);
                if (inputType && inputType.title) {
                    return inputType.title;
                }
            }
            // Fallback to formatted placeholder
            return placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        },
        formatPromptValue(value, placeholder) {
            // If it's a "link" type, return blank
            if (placeholder === 'link') {
                return '';
            }

            // If value is null or undefined, return empty string
            if (!value) {
                return '';
            }

            // Handle arrays - join with line breaks for visual separation
            if (Array.isArray(value)) {
                // Filter out empty values and join with line breaks
                const nonEmptyValues = value.filter(item => item !== null && item !== undefined && item !== '');
                if (nonEmptyValues.length === 0) {
                    return '';
                }
                
                // Format each value and join with double line breaks for clear separation
                const formattedValues = nonEmptyValues.map(item => {
                    let formattedItem = String(item);
                    // Replace h1-h6 tags with bold tags
                    formattedItem = formattedItem.replace(/<h[1-6][^>]*>/gi, '<strong>');
                    formattedItem = formattedItem.replace(/<\/h[1-6]>/gi, '</strong>');
                    return formattedItem;
                });
                
                // Join all values with double line breaks to create visual separation
                let formattedValue = formattedValues.join('<br><br>');
                
                // Count total lines across all values (including line breaks within each value)
                const totalLines = formattedValue.split(/<br\s*\/?>/i).length;
                if (totalLines > 4) {
                    // Split by <br> tags and limit to 4 lines
                    const parts = formattedValue.split(/<br\s*\/?>/i);
                    formattedValue = parts.slice(0, 4).join('<br>') + '...';
                }
                
                return formattedValue;
            }

            // Handle single values - convert to string and limit to 4 lines
            let formattedValue = String(value);
            
            // Replace h1-h6 tags with bold tags
            formattedValue = formattedValue.replace(/<h[1-6][^>]*>/gi, '<strong>');
            formattedValue = formattedValue.replace(/<\/h[1-6]>/gi, '</strong>');
            
            // Convert newlines to <br> tags for proper HTML rendering
            formattedValue = formattedValue.replace(/\n/g, '<br>');
            
            // Split into lines and limit to 4
            const lines = formattedValue.split(/<br\s*\/?>/i);
            if (lines.length > 4) {
                formattedValue = lines.slice(0, 4).join('<br>') + '...';
            }
            
            return formattedValue;
        }
    }
};
</script>
